import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { VbzButton } from "@smiles/voebiz-ui-kit";
import { useHistory } from "react-router-dom";

import { useUser } from "../../../context/UserContext";

import { VoebizHeader, Summary } from "../../../components";

import { getListOfValues } from "../../../services/listOfValues";

import { removeMask } from "../../../utils/documentUtils";
import { CompanyForm, DropdownsItems, handleFormChangeF, itemDescription } from "./type";
import CompanyAddress from "./CompanyAddress";
import CompanyInfo from "./CompanyInfo";

export default function Company() {
  const { t } = useTranslation("register_company");
  const { data, handler } = useUser();
  const history = useHistory();
  const path = "/cadastro";
  const typeOffice = "HOME";

  const [formData, setFormData] = useState<CompanyForm>({
    info: {
      cnpj: data.documentList?.[0].number || "00.000.000/0000-00",
      fantasyName: data.fantasyName || "",
      socialReason: data.socialReason || "",
      numberOfEmployees: data.numberOfEmployees || "",
      mainActivity: data.mainActivity || "",
      mainActivityDesc: data.mainActivityDesc || "",
      annualBudgetforTravel: data.annualBudgetforTravel || "",
      HowMeetedProgram: data.HowMeetedProgram || "",
      relationshipWithGOL: data.relationshipWithGOL || "",
      providedServicesToGol: data.providedServicesToGol || "",
      providedServicesToOthers: data.providedServicesToOthers || "",
    },
    address: {
      zipCode: data.addressList?.zipCode || "",
      state: data.addressList?.state || "",
      city: data.addressList?.city || "",
      streetAddress: data.addressList?.streetAddress || "",
      streetAddressNumber: data.addressList?.streetAddressNumber || "",
      complement: data.addressList?.complement || "",
      country: data.addressList?.country || "",
    },
  });

  const [formValidated, setFormValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdowns, setDropdowns] = useState<DropdownsItems>({
    employee_number: { stateName: "numberOfEmployees", items: [] },
    main_activity: { stateName: "mainActivity", items: [] },
    business_budget: { stateName: "annualBudgetforTravel", items: [] },
    how_did_you_found_us: { stateName: "HowMeetedProgram", items: [] },
  });
  const enableSendButton = useCallback(() => {
    const {
      socialReason,
      annualBudgetforTravel,
      numberOfEmployees,
      HowMeetedProgram,
      relationshipWithGOL,
      mainActivityDesc,
    } = formData.info;
    const { city, streetAddressNumber } = formData.address;

    if (
      relationshipWithGOL.length > 0 &&
      socialReason &&
      annualBudgetforTravel &&
      mainActivityDesc &&
      numberOfEmployees &&
      HowMeetedProgram &&
      city.length > 0 &&
      streetAddressNumber
    ) {
      setFormValidated(true);
    } else {
      setFormValidated(false);
    }
  }, [formData]);
  const goNext = async () => {
    setIsLoading(true);
    handler({
      ...data,
      ...formData.info,
      organizationName: "VB - VRG Linhas Aereas S.A.",
      addressList: {
        ...formData.address,
        type: typeOffice,
        preferential: true,
        zipCode: removeMask(formData.address.zipCode),
      },
    });
    setIsLoading(false);
    history.push(`${path}/admin`);
  };
  const handleContinue = (e: React.MouseEvent | React.FormEvent) => {
    e.preventDefault();
    goNext();
  };
  const toggle = () => setIsOpen(() => !isOpen);
  useEffect(() => {
    enableSendButton();
  }, [enableSendButton, formValidated]);
  const submitButton = () => (
    <div className="fadeIn-latest">
      <VbzButton
        id="btn_registerCompany"
        color="primary"
        text={t("input.button.continue.text")}
        disabled={!formValidated}
        onClick={handleContinue}
        onMouseDown={handleContinue}
        isLoading={isLoading}
      />
    </div>
  );

  const handleFormChange: handleFormChangeF = (form, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [form]: value,
    }));
  };

  const sortItems = (arr: itemDescription[]) =>
    [...arr].sort((a: itemDescription, b: itemDescription) => {
      if (a.id.indexOf("Até") > -1) {
        return -1;
      }
      const regex = /([0-9]{1,3}){1}/gm;
      const resultA = a.id.match(regex);
      const resultB = b.id.match(regex);
      if (resultA && resultB) {
        return parseInt(resultA[0], 10) - parseInt(resultB[0], 10);
      }
      return 0;
    });
  const loadListOfValues = async () => {
    const result = await getListOfValues(data.apiDomain);
    localStorage.setItem("listOfValuesData", JSON.stringify(result.data));
    const findElement = (el: string) => result.data.find(({ lovType }: { lovType: string }) => lovType === el);
    const { dadosEmpresa: employees } = findElement("GOL_VB_NUM_EMPLOYEES");
    const { dadosEmpresa: golMainActivity } = findElement("GOL_VB_MAIN_ACTIVITY");
    const { dadosEmpresa: golBusinessBudget } = findElement("GOL_VB_ANNUAL_TRV_BUDGET");
    const { dadosEmpresa: howToMetProgram } = findElement("GOL_VB_HOW_MET_PROGRAM");
    const sortedEmployees = sortItems(employees);
    const sortedBusinessBudget = sortItems(golBusinessBudget);
    /* eslint-disable camelcase */
    setDropdowns(({ employee_number, main_activity, business_budget, how_did_you_found_us }) => ({
      employee_number: { ...employee_number, items: sortedEmployees },
      main_activity: { ...main_activity, items: golMainActivity },
      business_budget: { ...business_budget, items: sortedBusinessBudget },
      how_did_you_found_us: { ...how_did_you_found_us, items: howToMetProgram },
    }));
  };

  useEffect(() => {
    loadListOfValues();
  }, []);

  return (
    <>
      <form className="vbz-lf">
        <div className="voebiz-register-progress-bar">
          <Summary isOpen={isOpen} handleClose={toggle} onExit={() => setIsOpen(false)} />
          <VoebizHeader
            id="drop_menu"
            steps={["Dados da empresa", "Administrador da conta"]}
            active={1}
            onNavClick={toggle}
            progress={50}
            shadow={false}
          />
        </div>

        <CompanyInfo data={formData.info} returnPath={path} onChange={handleFormChange} dropdowns={dropdowns} />
        <CompanyAddress data={formData.address} button={submitButton} onChange={handleFormChange} />
      </form>
    </>
  );
}
